import { useEffect, useRef, useState } from "react";
import { Bookmark, Download, FileText, Star, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function PliegoLogo({ compact = false }) {
  return <div className={`brand ${compact ? "brand--compact" : ""}`}>
    <img src="/pliego-mark.svg" alt="" />
    <span>Pliego</span>
  </div>;
}

export function Loading({ label = "Abriendo Pliego…" }) {
  return <div className="loading" role="status"><span className="loader-mark" /><span>{label}</span></div>;
}

export function EmptyState({ icon: Icon = FileText, title, body, action }) {
  return <div className="empty-state">
    <span className="empty-state__icon"><Icon size={24} /></span>
    <h3>{title}</h3>
    <p>{body}</p>
    {action}
  </div>;
}

export function PageHeader({ eyebrow, title, action, children }) {
  return <header className="page-header">
    <div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1>{children}</div>
    {action && <div className="page-header__action">{action}</div>}
  </header>;
}

export function Stars({ value = 0, count, size = 16 }) {
  return <span className="stars" aria-label={`${value || 0} de 5 estrellas`}>
    <span className="stars__row">{[1, 2, 3, 4, 5].map(index => <Star
      key={index}
      size={size}
      strokeWidth={2.2}
      fill={value >= index ? "currentColor" : "none"}
      className={value >= index - 0.5 && value < index ? "half" : ""}
    />)}</span>
    {count !== undefined && <small>{count ? `${Number(value).toFixed(1)} · ${count}` : "Sin valorar"}</small>}
  </span>;
}

export function RatingPicker({ value, onChange, disabled }) {
  const [draft, setDraft] = useState(value || 0);
  const dragging = useRef(false);
  const lastDraft = useRef(value || 0);

  useEffect(() => {
    if (!dragging.current) {
      setDraft(value || 0);
      lastDraft.current = value || 0;
    }
  }, [value]);

  const valueAt = (element, clientX) => {
    const rect = element.getBoundingClientRect();
    const position = Math.min(rect.width, Math.max(0, clientX - rect.left));
    return Math.min(5, Math.max(0.5, Math.ceil((position / rect.width) * 10) / 2));
  };
  const preview = next => {
    if (next === lastDraft.current) return;
    lastDraft.current = next;
    setDraft(next);
  };
  const commit = next => {
    preview(next);
    if (!disabled && next !== value) onChange(next);
  };

  return <div className={disabled ? "rating-picker disabled" : "rating-picker"}>
    <div
      className="rating-picker__rail"
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Puntuar material"
      aria-valuemin="0.5"
      aria-valuemax="5"
      aria-valuenow={draft || 0.5}
      aria-valuetext={draft ? `${String(draft).replace(".", ",")} de 5 estrellas` : "Sin valorar"}
      aria-disabled={disabled}
      onPointerDown={event => {
        if (disabled) return;
        dragging.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        preview(valueAt(event.currentTarget, event.clientX));
      }}
      onPointerMove={event => {
        if (disabled || !dragging.current) return;
        preview(valueAt(event.currentTarget, event.clientX));
      }}
      onPointerUp={event => {
        if (disabled || !dragging.current) return;
        dragging.current = false;
        commit(valueAt(event.currentTarget, event.clientX));
      }}
      onPointerCancel={() => {
        dragging.current = false;
        preview(value || 0);
      }}
      onKeyDown={event => {
        if (disabled) return;
        let next = draft || 0.5;
        if (event.key === "ArrowRight" || event.key === "ArrowUp") next = Math.min(5, next + 0.5);
        else if (event.key === "ArrowLeft" || event.key === "ArrowDown") next = Math.max(0.5, next - 0.5);
        else if (event.key === "Home") next = 0.5;
        else if (event.key === "End") next = 5;
        else return;
        event.preventDefault();
        commit(next);
      }}
    >
      {[1, 2, 3, 4, 5].map(star => <span className="rating-picker__star" key={star} aria-hidden="true">
        <Star className="rating-picker__outline" />
        <Star
          className={draft >= star ? "rating-picker__fill full" : draft >= star - 0.5 ? "rating-picker__fill half" : "rating-picker__fill empty"}
          fill="currentColor"
        />
      </span>)}
    </div>
    <strong>{draft ? String(draft).replace(".", ",") : "—"}</strong>
  </div>;
}

export function MaterialCard({ material, onToggleSave, onOpen, onOpenAuthor, onOpenTag, onDownload, showDownloadLabel = false }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(Boolean(material.viewer?.saved));
  const [saving, setSaving] = useState(false);
  const type = labelForType(material.documentType);
  return <article className={showDownloadLabel ? "material-card material-card--download" : "material-card"} onClick={() => onOpen ? onOpen(material) : navigate(`/material/${encodeURIComponent(material.id)}`)}>
    <div className="paper-preview" aria-hidden="true">
      <span className="paper-preview__fold" />
      <FileText size={29} />
      <small>{material.pageCount ? `${material.pageCount} pág.` : "PDF"}</small>
    </div>
    <div className="material-card__body">
      <div className="material-card__meta"><span>{type}</span><span>{formatBytes(material.sizeBytes)}</span></div>
      <h2>{material.title}</h2>
      <p className="material-card__subject">{material.subject?.name || material.program?.name}</p>
      <div className="material-card__footer">
        <button className="author-link" type="button" onClick={event => {
          event.stopPropagation();
          if (onOpenAuthor) onOpenAuthor(material.author);
          else navigate(`/perfil/${encodeURIComponent(material.author.did)}`);
        }}>
          <Avatar profile={material.author} size="small" />
          <span><strong>{material.author.displayName}</strong><small>@{material.author.handle}</small></span>
        </button>
        <span className="material-card__rating" aria-label={`${material.rating.average || 0} de 5 estrellas`}><Star size={15} fill="currentColor" /><strong>{material.rating.count ? Number(material.rating.average).toFixed(1) : "—"}</strong>{Boolean(material.rating.count) && <small>({material.rating.count})</small>}</span>
      </div>
    </div>
    <div className="material-card__actions">
      <button className={saved ? "icon-button active" : "icon-button"} type="button" disabled={saving} aria-label={saved ? "Quitar de guardados" : "Guardar"} onClick={async event => {
        event.stopPropagation();
        const next = !saved;
        setSaved(next);
        setSaving(true);
        try { await onToggleSave?.(material, next); } catch { setSaved(!next); } finally { setSaving(false); }
      }}><Bookmark size={20} fill={saved ? "currentColor" : "none"} /></button>
      {onDownload ? <button
        className={showDownloadLabel ? "download-button" : "icon-button"}
        type="button"
        aria-label="Descargar documento"
        onClick={event => { event.stopPropagation(); onDownload(material); }}
      ><Download size={20} />{showDownloadLabel && <span>Descargar documento</span>}</button> : <a
        className={showDownloadLabel ? "download-button" : "icon-button"}
        href={`${material.fileURL}?download=1`}
        download={material.originalFilename || "documento.pdf"}
        target="_blank"
        rel="noreferrer"
        aria-label="Descargar documento"
        onClick={event => event.stopPropagation()}
      ><Download size={20} />{showDownloadLabel && <span>Descargar documento</span>}</a>}
    </div>
  </article>;
}

export function Avatar({ profile, size = "regular" }) {
  const initial = (profile?.displayName || profile?.handle || "P").slice(0, 1).toUpperCase();
  return <span className={`avatar avatar--${size}`}>{profile?.avatarURL
    ? <img src={profile.avatarURL} alt="" loading="lazy" />
    : initial}</span>;
}

export function Overlay({ title, onClose, children, wide = false }) {
  return <div className="overlay" role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <section className={`overlay__panel ${wide ? "overlay__panel--wide" : ""}`}>
      <header><h2>{title}</h2><button className="icon-button" type="button" onClick={onClose} aria-label="Cerrar"><X /></button></header>
      <div className="overlay__content">{children}</div>
    </section>
  </div>;
}

export function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export function labelForType(value) {
  return ({ apuntes: "Apuntes", resumen: "Resumen", ejercicios: "Ejercicios", examen: "Examen", libro_autorizado: "Libro autorizado", otro: "Documento" })[value] || "Documento";
}
